import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

import type { AuthMethod, ConnectorBrandTheme } from '@optidev/shared-types';

interface AuthMethodTabsProps {
  authMethods: AuthMethod[];
  selected: string;
  onSelect: (id: string) => void;
  className?: string;
  brandTheme?: ConnectorBrandTheme;
}

/**
 * AuthMethodTabs - Renders tabs for switching between auth methods
 *
 * Examples:
 * - "Sign in with Google" vs "Service Account"
 * - "API Key" vs "OAuth"
 * - "Connection String" vs "Individual Fields"
 */
export function AuthMethodTabs({
  authMethods,
  selected,
  onSelect,
  className,
  brandTheme,
}: AuthMethodTabsProps) {
  // Don't render if only one auth method
  if (authMethods.length <= 1) {
    return null;
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex h-9 p-0.5 bg-muted rounded-lg">
        {authMethods.map((method) => (
          <button
            key={method.id}
            type="button"
            onClick={() => onSelect(method.id)}
            className={cn(
              "flex-1 h-full px-3 text-sm font-medium rounded-md transition-colors",
              method.id === selected
                ? "bg-background dark:bg-white/[0.1] text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {method.label}
          </button>
        ))}
      </div>

      {/* Show description of selected method */}
      {authMethods.map((method) => (
        method.id === selected && method.description && (
          <p key={method.id} className="text-xs text-muted-foreground">
            {method.description}
          </p>
        )
      ))}
    </div>
  );
}

export default AuthMethodTabs;
